import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import { IMG, VALUES } from "@/data/content";

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHero
        overline="About FarmingWave"
        lines={["Rooted in Science.", "Driven by Purpose."]}
        subtitle="FarmingWave was founded with a simple belief: farming should be smarter, more sustainable, and more profitable for everyone who depends on it — from small growers to large agribusinesses."
        testId="about-hero"
      />

      {/* Intro + image */}
      <section className="border-b border-border">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <Reveal className="md:col-span-6">
            <p className="text-lg text-muted-foreground leading-relaxed">
              We are a team of agronomists, engineers, and farm-management experts
              working at the intersection of traditional agriculture and modern
              controlled-environment technology. Our mission is to make
              hydroponics, polyhouse farming, and precision agronomy accessible,
              practical, and financially viable across India.
            </p>
          </Reveal>
          <Reveal delay={0.15} className="md:col-span-6">
            <img
              src="https://blogger.googleusercontent.com/img/b/R29vZ2xl/AVvXsEgRKQAY2jc_4GdzGBTc1mjLzkzUeBwQ0DCUdYs-oW7274Mrc2Q5zOQr2zy4X6GEHzEMh1w2tFvtfkHwuLLyfwqyCRCiLPsVjy4e9BWU2GzlWZUN3sYzZH13f7K6GnzV46YF60QTZyAmeVQTY39Zk6IwSulxhn70ZwkyGtq-s1Svd_CC0vDeJMd7KWC-0Pk/w320-h180/2.png"
              alt="Indian farmer in the field"
              className="w-full aspect-[4/3] object-cover"
            />
          </Reveal>
        </div>
      </section>

      {/* Our Story */}
      <section className="border-b border-border">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-12">
          <Reveal className="md:col-span-4">
            <span className="font-mono text-primary text-sm">01</span>
            <h2 className="mt-4 font-clash text-3xl md:text-4xl tracking-tight">Our Story</h2>
          </Reveal>
          <Reveal delay={0.1} className="md:col-span-7 md:col-start-6">
            <p className="text-lg text-muted-foreground leading-relaxed">
              What started as a small initiative to help local farmers adopt
              soilless farming techniques has grown into a full-fledged
              agricultural consultancy — serving clients across multiple states
              with hydroponic systems, polyhouse infrastructure, and complete
              farm management solutions.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="border-b border-border">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 grid grid-cols-1 md:grid-cols-2 border-l border-border">
          <Reveal>
            <div className="p-8 md:p-14 border-r border-b border-border h-full">
              <span className="font-mono text-primary text-sm">02</span>
              <h3 className="mt-4 font-clash text-2xl md:text-3xl">Our Mission</h3>
              <p className="mt-6 text-lg text-muted-foreground leading-relaxed">
                To empower farmers and agri-entrepreneurs with sustainable,
                high-yield, and resource-efficient farming systems — backed by
                science, technology, and hands-on expertise.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="p-8 md:p-14 border-r border-b border-border h-full">
              <span className="font-mono text-primary text-sm">03</span>
              <h3 className="mt-4 font-clash text-2xl md:text-3xl">Our Vision</h3>
              <p className="mt-6 text-lg text-muted-foreground leading-relaxed">
                To be India's most trusted name in controlled-environment
                agriculture and integrated farm services.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Values */}
      <section className="border-b border-border">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-24 md:py-32">
          <Reveal>
            <p className="overline text-primary">Our Values</p>
            <h2 className="mt-4 font-clash text-3xl md:text-4xl lg:text-5xl tracking-tight max-w-xl">
              The principles behind every farm we build.
            </h2>
          </Reveal>
          <Stagger className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-t border-l border-border">
            {VALUES.map((v, i) => (
              <StaggerItem key={v.title}>
                <div className="h-full p-8 border-r border-b border-border">
                  <span className="font-mono text-primary text-sm">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="mt-6 font-clash text-xl">{v.title}</h3>
                  <p className="mt-4 text-muted-foreground leading-relaxed">{v.text}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-[1400px] px-5 md:px-10 py-24 md:py-32">
        <Reveal>
          <h2 className="font-clash text-3xl md:text-5xl tracking-tight max-w-3xl">
            Want to work with a team that stays for the whole harvest?
          </h2>
          <Link
            to="/contact"
            data-testid="about-cta"
            className="group mt-10 overline inline-flex items-center gap-3 bg-primary text-primary-foreground px-8 py-4 font-bold transition-transform duration-300 hover:scale-[1.04]"
          >
            Book a Free Consultation
            <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
